// src/utils/imgur.js
//
// Imgur serves every image at full resolution unless you ask otherwise, and
// the artwork here averages ~4MB per JPEG (item3 is 5MB). The 12-card gallery
// grid is roughly 45MB of images; one comic episode is ~21MB across 8 panels.
//
// That matters because of how this site is reached: a visitor scans a QR code
// in a gallery, on venue wifi or 4G, standing in front of the painting. A 5MB
// hero image is 15-20 seconds on a mediocre connection. loading="lazy" and
// aspect-ratio stop the layout jumping while it arrives, but they cannot make
// it arrive faster.
//
// Imgur exposes resized variants by appending a single character to the image
// ID — no API call, no re-upload, no change to the source data:
//
//   https://i.imgur.com/owC0tFG.jpeg   →  original  (~4MB)
//   https://i.imgur.com/owC0tFGl.jpeg  →  640px     (~90kB)
//
// Using the right variant per context takes the gallery grid from ~45MB to
// roughly 1MB with no visible difference at the sizes actually rendered.

/**
 * Imgur's suffix codes. The names are Imgur's, not ours.
 */
export const IMGUR_SIZES = {
  t: 160,   // thumbnail — small square
  m: 320,   // medium    — gallery cards on mobile
  l: 640,   // large     — gallery cards on desktop, artist photo
  h: 1024,  // huge      — item page hero, comic panels
};

/**
 * Returns `url` rewritten to an Imgur size variant.
 *
 * Non-Imgur URLs and anything that doesn't match the expected shape are
 * returned untouched, so this is safe to apply blindly — if the images ever
 * move to another host, these calls degrade to no-ops rather than breaking.
 *
 * @param {string} url   e.g. https://i.imgur.com/owC0tFG.jpeg
 * @param {'t'|'m'|'l'|'h'|null} size  null returns the original
 */
export function imgurSize(url, size = "l") {
  if (!url || !size) return url;

  // Capture: prefix, image ID, extension. The ID is alphanumeric and 5-8 chars;
  // requiring that shape avoids double-suffixing an already-resized URL.
  const match = url.match(
    /^(https?:\/\/i\.imgur\.com\/)([a-zA-Z0-9]{5,8})(\.(?:jpe?g|png|gif|webp))$/i
  );
  if (!match) return url;

  const [, prefix, id, ext] = match;
  return `${prefix}${id}${size}${ext}`;
}

/**
 * Builds a srcset so the browser picks the right variant for the device's
 * width and pixel density — a phone at 390px CSS width with a 3x screen wants
 * roughly 1170 real pixels, not 320 and not 4000.
 *
 * Pair with a `sizes` attribute describing the rendered width, or the browser
 * assumes 100vw and over-fetches.
 */
export function imgurSrcSet(url, sizes = ["m", "l", "h"]) {
  const match = url?.match(
    /^(https?:\/\/i\.imgur\.com\/)([a-zA-Z0-9]{5,8})(\.(?:jpe?g|png|gif|webp))$/i
  );
  if (!match) return undefined;

  return sizes
    .map((s) => `${imgurSize(url, s)} ${IMGUR_SIZES[s]}w`)
    .join(", ");
}

/**
 * Named helpers, so display size lives with the component rather than as a
 * bare letter at each call site.
 */
export const artworkThumb = (url) => imgurSize(url, "m");   // gallery cards
export const artworkCard = (url) => imgurSize(url, "l");    // wider cards
export const artworkHero = (url) => imgurSize(url, "h");    // item page
export const artworkFull = (url) => imgurSize(url, null);   // lightbox / zoom
